import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Payment, PaymentMethod } from '@/lib/types';

interface AddPaymentPayload {
  personal_id: string;
  amount: number;
  method: PaymentMethod;
  paid_at: string;
  notes?: string;
  due_date?: string;
}

export function usePayments(studentId: string) {
  const queryClient = useQueryClient();

  const { data: payments = [], isLoading: loading } = useQuery({
    queryKey: ['payments', studentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('student_id', studentId)
        .order('paid_at', { ascending: false });
      if (error) throw error;
      return (data as Payment[]) ?? [];
    },
    enabled: !!studentId,
  });

  const { mutateAsync: addPayment } = useMutation({
    mutationFn: async (payload: AddPaymentPayload): Promise<Payment> => {
      const { data, error } = await supabase
        .from('payments')
        .insert({ ...payload, student_id: studentId })
        .select()
        .single();
      if (error) throw error;
      return data as Payment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', studentId] });
      queryClient.invalidateQueries({ queryKey: ['financial-overview'] });
    },
  });

  const { mutateAsync: deletePayment } = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('payments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', studentId] });
      queryClient.invalidateQueries({ queryKey: ['financial-overview'] });
    },
  });

  return { payments, loading, addPayment, deletePayment };
}
